<script setup lang="ts" generic="L extends PejerTableNames, TValue, T extends PejerTablesList<L>">
import type {
  AccessorKeyColumnDef,
  CellContext,
  Header,
  HeaderGroup,
  VisibilityState,
} from '@tanstack/vue-table'
import { FlexRender, getCoreRowModel, useVueTable } from '@tanstack/vue-table'
import DataStateDisplay from '../../DataState/DataStateDisplay.vue'
import Skeleton from '../../ui/skeleton/Skeleton.vue'

import type { BaseQueryArgs, ErrorObject, Order, PaginationArgs } from 'pejer/src/types/types'
import { computed, MaybeRef, onMounted, ref, unref, watch } from 'vue'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table'

import { ArrowDown, Eye, EyeOff, RefreshCcw, Search } from 'lucide-vue-next'

import { usePejerBase } from 'pejer/src/stores/pejerData'
import { PejerTableNames, PejerTablesList } from 'pejer/src/types/base'
import { SearchQuery } from 'pejer/src/types/types'
import { Groups } from '../../../tables'
import DataStateError from '../../DataState/DataStateError.vue'
import { DataTableEmits } from './DataTableEmits'
import FullDataItem from './FullDataItem.vue'
import HiddenColumns from './HiddenColumns.vue'

const props = defineProps<{
  columns: AccessorKeyColumnDef<PejerTablesList<L>, TValue>[]
  data: PejerTablesList<L>[]
  simpleGroupBy?: (keyof SearchQuery<PejerTablesList<L>>)[]
  pagination: PaginationArgs
  tableName: L
  canExpandRows?: boolean
  isLoading?: boolean
  totalSize?: number
  error?: MaybeRef<ErrorObject | null>
  isError?: MaybeRef<boolean>
  isFetching?: boolean
  query?: MaybeRef<BaseQueryArgs<PejerTablesList<L>>>
  initialColumnVisibility?: VisibilityState
  columnsVisibleByDefault?: MaybeRef<Record<string, boolean>>
  unsearchableColumns?: string[]
  dontFillMissingEntries?: boolean
  groups?: Groups
}>()

const emit = defineEmits<DataTableEmits<keyof T>>()

async function refresh() {
  emit('refresh')
}

// We need both of these as useVueTable will fill the rest of the state with false,
// therefore we need a separate ref to track the actual state of the columns.
// We do this to give the false state a meaning, as it means the column has been hidden by the user.
const visibleColumns = ref<VisibilityState>({ ...props.initialColumnVisibility })
const boundVisibleColumns = ref<VisibilityState>({ ...props.initialColumnVisibility })

const table = useVueTable({
  get data() {
    return props.data
  },
  get columns() {
    return props.columns
  },
  columnResizeMode: 'onChange',
  state: {
    get pagination() {
      return {
        pageIndex: props.pagination.page,
        pageSize: props.pagination.size,
      }
    },
    get columnVisibility() {
      return boundVisibleColumns.value
    },
  },
  getCoreRowModel: getCoreRowModel(),
  manualPagination: true,
})

const pejer = usePejerBase()

/**
 * Toggles the sorting of a column. We want the toggle to cycle through the following states:
 * * 1. Ascending
 * * 2. Descending
 * * 3. Back to default column sorting
 */
function toggleSortBy(columnId: keyof T) {
  if (props.query && unref(props.query).sortBy === columnId) {
    if (!unref(props.query).sorter) return

    if (unref(props.query).sorter === 'Ascending') {
      const defaultColumn = pejer.getDefaultColumns(props.tableName)
      emit('triggerSort', defaultColumn.sortingColumn as keyof T, 'Descending')
      return
    }

    emit('triggerSort', columnId, 'Ascending')
  } else {
    emit('triggerSort', columnId)
  }
}

function resetColumnsVisible() {
  const updatedVisibilityState: VisibilityState = {}

  const propsColumnsVisibleByDefault = unref(props.columnsVisibleByDefault)
  if (propsColumnsVisibleByDefault) {
    Object.keys(propsColumnsVisibleByDefault).forEach((col) => {
      updatedVisibilityState[formatNestedColumnKey(col)] =
        propsColumnsVisibleByDefault[formatNestedColumnKey(col)]
    })
  }

  visibleColumns.value = { ...updatedVisibilityState }
  boundVisibleColumns.value = {
    ...boundVisibleColumns.value,
    ...visibleColumns.value,
  }

  table.getVisibleLeafColumns().forEach((col) => {
    const id = formatNestedColumnKey(col.id)
    if (id in visibleColumns.value === false) boundVisibleColumns.value[id] = false
  })

  emit('updateColumnVisibility', updatedVisibilityState)
}

onMounted(() => {
  const newBoundVisibleColumns = { ...props.initialColumnVisibility }
  table.getVisibleLeafColumns().forEach((col) => {
    const id = formatNestedColumnKey(col.id)
    if (id in visibleColumns.value === false) newBoundVisibleColumns[id] = false
  })
  boundVisibleColumns.value = newBoundVisibleColumns
})

watch(
  () => props.columnsVisibleByDefault,
  () => {
    resetColumnsVisible()
  }
)

const idToGroupMap = computed(() => {
  const map: Record<string, string> = {}
  table.getAllLeafColumns().forEach((col) => {
    const columnDef = col.columnDef as AccessorKeyColumnDef<T, TValue>
    if (columnDef?.accessorKey) map[col.id] = col.columnDef.meta?.group ?? '_'
  })
  return map
})

const columnGroupsSize = computed(() => {
  const groups = new Set<string>()
  table.getAllLeafColumns().forEach((col) => {
    const group = col.columnDef.meta?.group ?? '_'
    groups.add(group)
  })
  return groups.size
})

function formatNestedColumnKey(key: string) {
  return key.replace('.', '_')
}

function showColumn(col: string, show = true) {
  visibleColumns.value = {
    ...visibleColumns.value,
    [formatNestedColumnKey(col)]: show,
  }
  boundVisibleColumns.value = {
    ...boundVisibleColumns.value,
    ...visibleColumns.value,
  }
  emit('updateColumnVisibility', visibleColumns.value)
}

function triggerSearch(column: string) {
  emit('updateColumnSearch', column as keyof T)
}

function shouldHideColumn(column: string) {
  return !!props.unsearchableColumns?.includes(column)
}

/**
 * * Gets the headers of a header group and groups them by their group id.
 */
function getGroupedHeaders(headerGroup: HeaderGroup<PejerTablesList<L>>) {
  const result: {
    header: Header<T, unknown>
    groupId: string
    colspan: number
  }[] = []
  let currentGroup = null

  headerGroup.headers.forEach((header) => {
    const groupId = header.column.columnDef.meta?.group ?? '_'
    if (currentGroup && groupId === currentGroup.groupId) {
      currentGroup.colspan += 1
    } else {
      if (currentGroup) {
        result.push(currentGroup)
      }
      currentGroup = {
        header,
        groupId,
        colspan: 1,
      }
    }
  })

  if (currentGroup) {
    result.push(currentGroup)
  }

  return result
}

// Add this line to create a reactive Set for expanded rows
const expandedRows = ref(new Set<string>())

/**
 * Toggles the expanded state of a row.
 */
function toggleRow(rowId: string) {
  if (!props.canExpandRows) return
  if (expandedRows.value.has(rowId)) {
    expandedRows.value.delete(rowId)
  } else {
    expandedRows.value.add(rowId)
  }
}

watch(
  () => props.data,
  () => {
    expandedRows.value = new Set<string>()
  }
)

/**
 * @deprecated This function should not be used anymore, as passing state should not be done through props, but rather injected if needed.
 */
function mergeProps<T>(cellContext: CellContext<T, unknown>): CellContext<T, unknown> {
  return {
    ...cellContext,
    options: { oneLine: true },
    emits: {
      loadMore: (page: number, pageSize: number) => emit('loadMore', page, pageSize),
      // reload: async () => await emit('reload'),
      triggerSort: (column: string, sorter?: Order) => {
        emit('triggerSort', column as any, sorter as any)
      },
      updateColumnVisibility: (visibility: VisibilityState) =>
        emit('updateColumnVisibility', visibility),
      updateColumnSearch: (column: string) => emit('updateColumnSearch', column as any),
    },
  }
}
</script>
<template>
  <!-- <SectionCard> -->
  <HiddenColumns
    :columns="
      table
        .getAllLeafColumns()
        .map((col) => col.columnDef as AccessorKeyColumnDef<T, TValue>)
        .filter((col) => !col.meta?.hideFromTable)
    "
    :visibleColumns="boundVisibleColumns"
    :groupColors="props.groups"
    :unsearchableColumns="props.unsearchableColumns"
    @triggerSearch="triggerSearch"
    @resetDisplayedColumns="resetColumnsVisible"
    @showColumn="showColumn"
  />

  <div>
    <Table class="w-full overflow-x-scroll relative">
      <TableHeader>
        <TableRow class="border-0" v-if="columnGroupsSize != 1">
          <TableHead
            v-for="group in getGroupedHeaders(table.getHeaderGroups()[0])"
            :key="group.header.id"
            :colspan="group.colspan"
            class="px-0 relative"
          >
            <span
              v-if="group.header.column.columnDef.meta?.group"
              class="sticky left-0 font-normal"
              :style="{
                color:
                  props.groups?.[idToGroupMap[group.header.column.id]]?.color.accent || 'lightGray',
              }"
            >
              {{ idToGroupMap[group.header.column.id] }}
            </span>

            <div
              class="w-full h-1 absolute bottom-0 rounded-t-md"
              v-if="group.header.column.columnDef.meta?.group"
              :style="{
                backgroundColor:
                  props.groups?.[idToGroupMap[group.header.column.id]]?.color.accent || '',
              }"
            ></div>
          </TableHead>
        </TableRow>
        <TableRow v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
          <TableHead v-for="header in headerGroup.headers" :key="header.id" class="px-0">
            <div class="relative h-full flex items-center">
              <div
                v-if="header.column.id !== 'actions'"
                class="w-full h-full flex items-center justify-between rounded-sm group/cell pl-2"
                @click.stop.prevent="
                  header.column.columnDef.meta?.sortable &&
                  'accessorKey' in header.column.columnDef &&
                  toggleSortBy(header.column.columnDef.accessorKey as keyof T)
                "
                :class="
                  header.column.columnDef.meta?.sortable ? 'cursor-pointer hover:bg-gray-200' : ''
                "
              >
                <FlexRender :render="header.column.columnDef.header" :props="header.getContext()" />
                <div class="flex items-center">
                  <div class="flex flex-col items-end">
                    <button
                      class="hover:bg-red-200 rounded-sm flex-shrink-0 p-1 ml-2 group-hover/cell:opacity-100 opacity-0 transition-opacity duration-200 group/visibility relative"
                      @click.prevent.stop="
                        () => {
                          showColumn(header.column.id, false)
                        }
                      "
                    >
                      <div class="w-[14px] h-[14px]"></div>
                      <Eye
                        class="group-hover/visibility:hover:opacity-0 opacity-100 absolute z-10 top-1/2 -translate-y-1/2"
                        :size="14"
                      />
                      <EyeOff
                        class="group-hover/visibility:hover:opacity-100 opacity-0 absolute z-10 top-1/2 -translate-y-1/2"
                        :size="14"
                      />
                    </button>
                    <button
                      v-if="header.column.columnDef.meta?.searchable !== false"
                      :disabled="shouldHideColumn(header.column.id)"
                      class="group-hover/cell:opacity-100 opacity-0 transition-all hover:bg-blue-500 hover:text-white p-[.25rem] rounded-sm w-min"
                      :class="{
                        'cursor-pointer': !shouldHideColumn(header.column.id),
                        'cursor-not-allowed': shouldHideColumn(header.column.id),
                      }"
                      @click.prevent.stop="
                        () => {
                          triggerSearch(
                            (header.column.columnDef as AccessorKeyColumnDef<T, TValue>)
                              .accessorKey as string
                          )
                        }
                      "
                    >
                      <Search :size="14" />
                    </button>
                  </div>
                  <div
                    class="ml-2"
                    v-if="
                      'accessorKey' in header.column.columnDef &&
                      header.column.columnDef.accessorKey === unref(query)?.sortBy &&
                      !unref(error)
                    "
                  >
                    <ArrowDown
                      v-if="!isFetching"
                      :style="{
                        transition: 'all',
                        transform: unref(query)?.sorter === 'Ascending' ? 'rotate(180deg)' : '',
                      }"
                      :size="14"
                    />
                    <RefreshCcw v-else class="animate-spin scale-[-1]" :size="14" />
                  </div>
                  <!-- Resizer -->
                  <div
                    @mousedown.prevent.stop="header.getResizeHandler()?.($event)"
                    @touchstart.prevent.stop="header.getResizeHandler()?.($event)"
                    @click.prevent.stop
                    style="
                      cursor: col-resize;
                      width: 6px;
                      height: 100%;
                      position: absolute;
                      right: 0;
                    "
                  ></div>
                </div>
              </div>
              <div
                v-else
                class="cursor-pointer hover:bg-gray-200 w-full h-full text-center flex justify-center items-center rounded-sm min-w-fit"
                @click="refresh()"
              >
                <RefreshCcw :size="20" :class="isFetching ? 'animate-spin scale-[-1]' : ''" />
              </div>
            </div>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <DataStateDisplay
          :isError="(unref(isError) || !!unref(error)) && pagination.page - 1 >= data.length"
          :error="error"
          :isLoading="isLoading"
        >
          <!-- Show skeleton loader -->
          <template #loading>
            <TableRow class="" v-for="rowIndex in Math.min(pagination.size, 10)" :key="rowIndex">
              <TableCell class="h-12 p-2 align-middle" :colSpan="columns.length">
                <Skeleton class="w-full h-[20px]" />
              </TableCell>
            </TableRow>
          </template>
          <template #error>
            <TableRow
              class="border-0"
              v-for="rowIndex in Math.min(pagination.size, 10) - 1"
              :key="rowIndex"
            >
              <TableCell :colspan="columns.length" class="h-12">
                <DataStateError
                  v-if="rowIndex === Math.floor(Math.min(pagination.size, 10) / 2)"
                  class="w-full flex"
                  :error="unref(error)"
                />
              </TableCell>
            </TableRow>
            <!-- <td :colspan="columns.length"></td> -->
          </template>
          <!-- No results -->
          <template v-if="table.getRowModel().rows?.length === 0">
            <TableRow
              class="border-0"
              v-for="rowIndex in Math.min(pagination.size, 10)"
              :key="rowIndex"
            >
              <TableCell :colSpan="columns.length" class="h-12 text-center">
                <span v-if="rowIndex === Math.floor(Math.min(pagination.size, 10) / 2)"
                  >No results.</span
                >
              </TableCell>
            </TableRow>
          </template>
          <!-- Data rows -->
          <template v-else>
            <template
              v-for="row in table.getRowModel().rows"
              :key="(pagination.page * pagination.size) / 10 + row.id"
            >
              <TableRow
                @click="toggleRow(row.id)"
                :class="{
                  'transition-transform bg-gray-100 border-b-0': expandedRows.has(row.id),
                  'transition-transform cursor-pointer hover:scale-[99.5%] ': props.canExpandRows,
                  'transition-transform hover:bg-white': !props.canExpandRows,
                }"
              >
                <TableCell
                  v-for="cell in row.getVisibleCells()"
                  :key="cell.id"
                  class="p-1"
                  :style="{
                    maxWidth: cell.column.id !== 'actions' ? cell.column.getSize() + 'px' : '',
                    width: cell.column.id === 'actions' ? 'fit-content' : '',
                  }"
                >
                  <FlexRender
                    :render="cell.column.columnDef.cell"
                    :props="mergeProps(cell.getContext())"
                  />
                </TableCell>
              </TableRow>
              <FullDataItem
                v-if="props.canExpandRows"
                :row="row"
                :table-name="tableName"
                :columns="columns"
                :is-expanded="expandedRows.has(row.id)"
              />
            </template>
            <template v-if="!dontFillMissingEntries">
              <TableRow
                v-for="index in pagination.size - table.getRowModel().rows?.length"
                :key="index"
              >
                <div class="h-8 p-1 align-middle w-full"></div>
              </TableRow>
            </template>
          </template>
        </DataStateDisplay>
      </TableBody>
    </Table>
  </div>
  <!-- </SectionCard> -->
</template>
